/**
 * Alpaca API configuration module
 * Handles environment variables and trading mode (paper vs. live)
 */

export interface AlpacaConfig {
  keyId: string;
  secretKey: string;
  paper: boolean;
  baseUrl: string;
  dataBaseUrl: string;
  feedDataBaseUrl?: string;
}

// Import environment helper
import { getAlpacaEnv } from './env';

// Initialize with environment values
const envConfig = getAlpacaEnv();

// Default configuration
let config: AlpacaConfig = {
  keyId: envConfig.keyId,
  secretKey: envConfig.secretKey,
  paper: envConfig.paper,
  baseUrl: envConfig.baseUrl,
  dataBaseUrl: envConfig.dataBaseUrl,
};

/**
 * Initialize the Alpaca API with configuration
 */
export function initialize(newConfig: Partial<AlpacaConfig>): void {
  config = { ...config, ...newConfig };
  
  if (!config.keyId || !config.secretKey) {
    // Only show warning in production environments to avoid confusion during development
    if (typeof window !== 'undefined' && window.location.hostname !== 'localhost') {
      console.warn('ALPACA_KEY_ID/ALPACA_SECRET not set – Alpaca API will fail if utilized');
    }
  }
  
  // Log the current configuration (without secrets)
  console.log(`Alpaca API configuration initialized: 
    Environment: ${config.paper ? 'PAPER' : 'LIVE'}
    Trading URL: ${config.baseUrl}
    Data URL: ${config.dataBaseUrl}
    Feed URL: ${config.feedDataBaseUrl || 'default'}
  `);
}

/**
 * Get the current Alpaca configuration
 */
export function getConfig(): AlpacaConfig {
  return { ...config };
}

/**
 * Set the trading mode to paper or live
 */
export function setPaperMode(isPaper: boolean): void {
  config.paper = isPaper;
  
  // Update the base URLs automatically
  config.baseUrl = isPaper 
    ? 'https://paper-api.alpaca.markets' 
    : 'https://api.alpaca.markets';
    
  // Data URL is the same for both environments
  config.dataBaseUrl = 'https://data.alpaca.markets';
}

/**
 * Get the current trading mode
 */
export function getTradingMode(): 'paper' | 'live' {
  return config.paper ? 'paper' : 'live';
}

/**
 * Check if the configuration is valid
 */
export function isConfigValid(): boolean {
  return Boolean(config.keyId && config.secretKey);
}

/**
 * Get the appropriate base URLs based on configuration
 */
export function getBaseUrls(): { baseUrl: string; dataBaseUrl: string } {
  return { 
    baseUrl: config.baseUrl, 
    dataBaseUrl: config.dataBaseUrl 
  };
}